import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { hydrateProgressStore } from './store/progressStore.js';
import { hydrateUIVariantStore } from './store/uiVariantStore.js';
import { hydrateDeviceModeStore, useDeviceModeStore, selectDeviceRole } from './store/deviceModeStore.js';
import { getPagesForVariant } from './pages/pageRegistry.js';
import { useUIVariant } from './hooks/useUIVariant.js';
import { t } from './i18n/el.js';
import { SYNC_ENABLED, DEV_AUTH } from './data/constants.js';
import { getEntryChoice } from './native/entryChoice.js';

// Dictionary surfaces are variant-agnostic (single shared implementation),
// so they live outside the page registry and are lazy-loaded directly.
const DictionaryPage = lazy(() => import('./pages/DictionaryPage.jsx'));
const DictionaryGamePage = lazy(() => import('./pages/DictionaryGamePage.jsx'));
const MakeupPage = lazy(() => import('./pages/MakeupPage.jsx'));
const ForestPage = lazy(() => import('./pages/ForestPage.jsx'));

// Lightweight sync UI (status indicator + sign-in badge + save-progress banner)
// + the parent sign-in screen + first-run gateway. Lazily loaded + gated on
// SYNC_ENABLED so the offline-only build never ships them.
const SyncOverlay = lazy(() => import('./components/common/SyncOverlay.jsx'));
const AccountPage = lazy(() => import('./pages/AccountPage.jsx'));
const WelcomePage = lazy(() => import('./pages/WelcomePage.jsx'));
const ParentsPage = lazy(() => import('./pages/ParentsPage.jsx'));
const ChildInsightsPage = lazy(() => import('./pages/ChildInsightsPage.jsx'));
const ParentGate = lazy(() => import('./components/common/ParentGate.jsx'));

/**
 * First-run gateway redirect. Sends the user to /welcome exactly ONCE per
 * session when they haven't yet chosen guest-vs-account, then never again (so
 * navigating away from /welcome works and the choice — once persisted — keeps
 * the gateway from reappearing on later boots). Skipped under dev auto-login,
 * which authenticates on its own.
 */
function FirstRunGate({ needsWelcome }) {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (needsWelcome && !done.current) {
      done.current = true;
      navigate('/welcome', { replace: true });
    }
  }, [needsWelcome, navigate]);
  return null;
}

/**
 * Parent-device boot redirect. A 'parent'-role device opens straight into the
 * parent area: ONCE per app session, if we boot at the home route, bounce to
 * /parents. Only once (a `done` ref) so the parent can still navigate back to
 * '/' afterwards to "play as a child" — the kid UI stays reachable.
 */
function ParentDeviceGate({ active }) {
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (active && !done.current && location.pathname === '/') {
      done.current = true;
      navigate('/parents', { replace: true });
    }
  }, [active, location.pathname, navigate]);
  return null;
}

export default function App() {
  const [bootReady, setBootReady] = useState(false);
  const [entryChoice, setEntryChoiceState] = useState(null);
  const { variant, ready: variantReady } = useUIVariant();
  const deviceRole = useDeviceModeStore(selectDeviceRole);

  useEffect(() => {
    (async () => {
      const [, , , choice] = await Promise.all([
        hydrateProgressStore(),
        hydrateUIVariantStore(),
        SYNC_ENABLED ? hydrateDeviceModeStore() : Promise.resolve(),
        SYNC_ENABLED ? getEntryChoice() : Promise.resolve(null),
      ]);
      setEntryChoiceState(choice);
      setBootReady(true);
      // Online sync bootstrap: fire-and-forget AFTER hydration so it never
      // blocks boot (offline-first). Dynamically imported + gated on
      // SYNC_ENABLED, so the offline-only build never pulls in the sync code.
      if (SYNC_ENABLED) {
        import('./native/syncBootstrap.js')
          .then((m) => m.bootstrapSync())
          .catch(() => {});
      }
    })();
  }, []);

  if (!bootReady || !variantReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary-700 text-kid-base">{t.errors.loading}</div>
      </div>
    );
  }

  const {
    HomePage,
    PlaySelectionPage,
    SessionPage,
    SessionCompletePage,
    ProgressPage,
    RuleListPage,
    RulePlayPage,
  } = getPagesForVariant(variant);

  // Show the gateway once for a fresh online install (no choice made yet). Dev
  // auto-login handles its own auth, so it bypasses the gateway entirely.
  const needsWelcome = SYNC_ENABLED && !DEV_AUTH.enabled && entryChoice == null;
  // A 'parent'-role device boots into the parent area (unless the welcome
  // gateway is taking precedence for a fresh install).
  const parentDeviceBoot = SYNC_ENABLED && !needsWelcome && deviceRole === 'parent';

  return (
    <BrowserRouter>
      {SYNC_ENABLED && <FirstRunGate needsWelcome={needsWelcome} />}
      {SYNC_ENABLED && <ParentDeviceGate active={parentDeviceBoot} />}
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-primary-700 text-kid-base">
              {t.errors.loading}
            </div>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/play/:grade" element={<PlaySelectionPage />} />
          <Route path="/session/:grade" element={<SessionPage />} />
          <Route
            path="/mistakes/:grade"
            element={<SessionPage mode="mistakes" />}
          />
          <Route
            path="/history/:grade"
            element={<SessionPage mode="history" />}
          />
          <Route
            path="/focused/:grade/:tag"
            element={<SessionPage mode="focused" />}
          />
          <Route path="/session-complete" element={<SessionCompletePage />} />
          <Route path="/grammar/:grade" element={<RuleListPage />} />
          <Route path="/grammar/:grade/:ruleId" element={<RulePlayPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/dictionary" element={<DictionaryPage />} />
          <Route path="/dictionary-game" element={<DictionaryGamePage />} />
          <Route path="/makeup/:date" element={<MakeupPage />} />
          <Route path="/forest" element={<ForestPage />} />
          {SYNC_ENABLED && (
            <Route
              path="/account"
              element={
                <ParentGate>
                  <AccountPage />
                </ParentGate>
              }
            />
          )}
          {SYNC_ENABLED && <Route path="/welcome" element={<WelcomePage />} />}
          {SYNC_ENABLED && (
            <Route
              path="/parents"
              element={
                <ParentGate>
                  <ParentsPage />
                </ParentGate>
              }
            />
          )}
          {SYNC_ENABLED && (
            <Route
              path="/parents/:profileId/insights"
              element={
                <ParentGate>
                  <ChildInsightsPage />
                </ParentGate>
              }
            />
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {SYNC_ENABLED && (
        <Suspense fallback={null}>
          <SyncOverlay />
        </Suspense>
      )}
    </BrowserRouter>
  );
}
